import { normalizeWorkspace, withWorkspace } from './selected-workspace.js'
// Held reply events: list them and record what to do with one. No credentials in URLs.
// GET /api/reply-events/held, POST /api/reply-events/:id/resolve (backend/contracts/reply-events-held.md).
export class ReplyEventsClientError extends Error {
  constructor(message, code, status = 0) {
    super(message)
    this.code = code
    this.status = status
  }
}

const fail = (message, code = 'INVALID_RESPONSE', status = 0) => { throw new ReplyEventsClientError(message, code, status) }
const exact = (x, keys) => x !== null && typeof x === 'object' && !Array.isArray(x) && Object.keys(x).length === keys.length && keys.every(k => Object.hasOwn(x, k))
const id = x => typeof x === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(x)
const count = x => Number.isSafeInteger(x) && x >= 0
const time = x => Number.isSafeInteger(x) && x > 0 && x <= 8640000000000000
const text = (x, max) => typeof x === 'string' && x.length <= max
const code = x => typeof x === 'string' && /^[A-Z_]{1,100}$/.test(x)

export const HELD_STATUSES = ['unmatched', 'ambiguous', 'pending']
export const RESOLVE_ACTIONS = ['no_action_needed', 'handled_manually']
export const NOTE_MAX = 1000
export const PAGE_SIZE = 100

const EVENT_KEYS = ['eventId', 'status', 'resultCode', 'eventType', 'receivedAt', 'mailbox', 'senderAddress', 'subject']

function validEvent(e) {
  return exact(e, EVENT_KEYS) && id(e.eventId) && HELD_STATUSES.includes(e.status) &&
    (e.resultCode === null || code(e.resultCode)) && text(e.eventType, 100) && e.eventType.length > 0 && time(e.receivedAt) &&
    (e.mailbox === null || (exact(e.mailbox, ['mailboxId', 'address']) && id(e.mailbox.mailboxId) && text(e.mailbox.address, 254) && e.mailbox.address.length > 0)) &&
    (e.senderAddress === null || (text(e.senderAddress, 254) && e.senderAddress.length > 0)) &&
    (e.subject === null || text(e.subject, 300))
}

export function validHeldList(v) {
  if (!exact(v, ['schemaVersion', 'summary', 'events', 'hasMore']) || v.schemaVersion !== 1 || typeof v.hasMore !== 'boolean') return false
  const s = v.summary
  if (!exact(s, ['held', 'byStatus', 'lastReceivedAt']) || !count(s.held) || !(s.lastReceivedAt === null || time(s.lastReceivedAt))) return false
  if (!exact(s.byStatus, HELD_STATUSES) || !HELD_STATUSES.every(k => count(s.byStatus[k])) || HELD_STATUSES.reduce((n, k) => n + s.byStatus[k], 0) !== s.held) return false
  if (!Array.isArray(v.events) || v.events.length > PAGE_SIZE || v.events.length > s.held || !v.events.every(validEvent)) return false
  if (new Set(v.events.map(e => e.eventId)).size !== v.events.length) return false
  return v.hasMore === (s.held > v.events.length)
}

export function validResolution(v, eventId, action) {
  return exact(v, ['resolved', 'eventId', 'action']) && v.resolved === true && v.eventId === eventId && v.action === action
}

// Not connected yet means the route is missing (404) or the service is off (503). It is never "empty".
export const isNotConnected = e => e instanceof ReplyEventsClientError && (e.status === 404 || e.status === 503)
export const isConflict = e => e instanceof ReplyEventsClientError && e.status === 409 && (e.code === 'WINNR_EVENT_ALREADY_RESOLVED' || e.code === 'WINNR_EVENT_NOT_HELD')

const MESSAGES = {
  401: 'Sign in again to view held replies.',
  403: 'This account is not allowed to view held replies.',
  409: 'Someone else already resolved this reply, or it is no longer held.',
  429: 'Too many requests. Wait a moment and try again.',
}

export function createReplyEventsClient({ baseUrl, getToken, expectedUserId, expectedSessionId, fetchImpl = fetch, allowLocal = false, timeoutMs = 15000, workspaceId = null } = {}) {
  let workspace
  try { workspace = normalizeWorkspace(workspaceId) } catch { fail('The Engine reply service configuration is invalid.', 'INVALID_CONFIG') }
  let url
  try { url = new URL(baseUrl) } catch { fail('The Engine reply service is not configured.', 'INVALID_CONFIG') }
  const local = allowLocal && url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
  const sessionId = x => typeof x === 'string' && /^[A-Za-z0-9_-]{1,120}$/.test(x)
  if ((url.protocol !== 'https:' && !local) || url.username || url.password || url.pathname !== '/' || url.search || url.hash ||
    ![expectedUserId, expectedSessionId].every(sessionId) || typeof getToken !== 'function' || typeof fetchImpl !== 'function' ||
    !Number.isSafeInteger(timeoutMs) || timeoutMs <= 0 || timeoutMs > 60000) fail('The Engine reply service configuration is invalid.', 'INVALID_CONFIG')

  const tokenMatches = token => {
    try {
      const parts = token.split('.')
      if (parts.length !== 3 || parts.some(p => !p) || !/^[A-Za-z0-9_-]{1,16384}$/.test(parts[1])) return false
      const b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/')
      const raw = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))
      const claims = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(raw, c => c.charCodeAt(0))))
      return claims?.sub === expectedUserId && claims?.sid === expectedSessionId
    } catch { return false }
  }

  async function request(path, { signal, body } = {}) {
    if (signal?.aborted) throw new DOMException('Request cancelled.', 'AbortError')
    const controller = new AbortController()
    let stopWith
    const stop = new Promise((_, reject) => { stopWith = reject })
    stop.catch(() => {})
    const halt = error => { stopWith(error); controller.abort() }
    const cancel = () => halt(new DOMException('Request cancelled.', 'AbortError'))
    signal?.addEventListener('abort', cancel, { once: true })
    const post = body !== undefined
    const timer = setTimeout(() => halt(new ReplyEventsClientError(post ? 'The reply service took too long to answer. Reload to see whether the reply was resolved.' : 'The reply service took too long to answer.', 'REQUEST_TIMEOUT')), timeoutMs)
    // The whole call is bounded: token, request and body read all race the same stop.
    const bounded = promise => Promise.race([promise, stop])
    try {
      let token
      try { token = await bounded(Promise.resolve().then(() => getToken())) } catch (e) {
        if (e instanceof ReplyEventsClientError || e?.name === 'AbortError') throw e
        fail('Sign in again to view held replies.', 'AUTHENTICATION_REQUIRED', 401)
      }
      if (typeof token !== 'string' || !tokenMatches(token)) fail('Your sign-in changed. Reload the page.', 'AUTH_SESSION_CHANGED', 401)
      let response
      try {
        response = await bounded(fetchImpl(url.origin + path, {
          method: post ? 'POST' : 'GET',
          ...(post ? { body: JSON.stringify(body) } : {}),
          signal: controller.signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }))
      } catch (e) {
        if (e instanceof ReplyEventsClientError || e?.name === 'AbortError') throw e
        fail('The reply service could not be reached.', 'NETWORK_UNAVAILABLE')
      }
      let data = null
      try {
        const raw = await bounded(response.text())
        if (raw.length > 1048576) fail('The reply service returned an invalid response.')
        data = raw ? JSON.parse(raw) : null
      } catch (e) {
        if (e instanceof ReplyEventsClientError || e?.name === 'AbortError') throw e
        if (response.ok) fail('The reply service returned an invalid response.')
      }
      if (!response.ok) {
        const errorCode = data && typeof data === 'object' && code(data.error) ? data.error : 'REPLY_REQUEST_FAILED'
        fail(MESSAGES[response.status] || 'The reply request failed.', errorCode, response.status)
      }
      return data
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
    }
  }

  return Object.freeze({
    async list({ signal } = {}) {
      const value = await request(withWorkspace('/api/reply-events/held', workspace), { signal })
      if (!validHeldList(value)) fail('The reply service returned an invalid list.')
      return value
    },
    async resolve(eventId, { action, note } = {}, { signal } = {}) {
      if (!id(eventId)) fail('Choose a valid reply.', 'INVALID_ID')
      if (!RESOLVE_ACTIONS.includes(action)) fail('Choose what to record.', 'INVALID_ACTION')
      const trimmed = typeof note === 'string' ? note.trim() : ''
      if (!trimmed) fail('Write a note before resolving.', 'NOTE_REQUIRED')
      if (trimmed.length > NOTE_MAX) fail(`Keep the note to ${NOTE_MAX} characters.`, 'NOTE_TOO_LONG')
      const value = await request(`/api/reply-events/${eventId}/resolve`, { signal, body: { action, note: trimmed } })
      if (!validResolution(value, eventId, action)) fail('The reply service returned an invalid answer. Reload to see whether the reply was resolved.')
      return value
    },
  })
}
